// Yousef's cipher portrait, redrawn natively as drifting code glyphs.
// No iframe, no controls — the glyphs float directly over the hero, in the
// site's own sea-glass-on-ink palette so it reads as part of the page.

const GLYPHS = [
  "00",
  "0A",
  "1F",
  "2B",
  "3C",
  "4D",
  "5E",
  "6F",
  "7A",
  "8B",
  "BE",
  "C0",
  "D1",
  "E2",
  "F3",
  "01",
];
const BUCKET_OPACITY = [0.2, 0.5, 0.94];

const hash = (x, y, salt) => {
  const value = Math.sin(x * 12.9898 + y * 78.233 + salt * 37.719) * 43758.5453;
  return value - Math.floor(value);
};

const readVar = (probe, name, fallback) => {
  probe.style.color = `var(${name})`;
  const value = getComputedStyle(probe).color;
  return value || fallback;
};

export default function initCipherPortrait() {
  const host = document.querySelector("[data-cipher-portrait]");
  if (!host) return;

  const canvas = document.createElement("canvas");
  canvas.setAttribute("role", "img");
  canvas.setAttribute(
    "aria-label",
    "Portrait of Yousef formed from continuously moving code characters"
  );
  host.appendChild(canvas);

  const context = canvas.getContext("2d", { alpha: true });
  if (!context) return;

  const probe = document.createElement("span");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText =
    "position:absolute;width:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none";
  host.appendChild(probe);

  const maskCanvas = document.createElement("canvas");
  const maskContext = maskCanvas.getContext("2d", { willReadFrequently: true });

  // The hidden source is itself a cipher portrait. Sampling it at native
  // resolution preserves the full head-and-shoulders silhouette at small sizes.
  const image = host.querySelector("[data-cipher-src]");
  if (!image) return;

  let inkGlyph = "rgba(121, 217, 208, 1)";
  let width = 1;
  let height = 1;
  let dpr = 1;
  let fontSize = 7;
  let phase = 0;
  let motionElapsed = 0;
  let previousTime = performance.now();
  let lastRenderedTime = 0;
  let frame = 0;
  let imageReady = false;
  let visible = true;
  let particles = [];
  let particleGroups = [[], [], []];
  const pointer = { x: 0, y: 0, targetX: 0, targetY: 0, active: false };

  // Honour the visitor's system preferences: someone on reduced-motion or a
  // metered/data-saver connection gets a single still frame, not a loop.
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const reducedData = window.matchMedia("(prefers-reduced-data: reduce)");
  const wantsStill = () => reducedMotion.matches || reducedData.matches;

  const readColors = () => {
    inkGlyph = readVar(probe, "--accent", "rgba(121, 217, 208, 1)");
  };

  const buildParticles = () => {
    if (!imageReady || width < 2 || height < 2) return;
    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;
    maskCanvas.width = sourceWidth;
    maskCanvas.height = sourceHeight;
    maskContext.clearRect(0, 0, sourceWidth, sourceHeight);
    maskContext.drawImage(image, 0, 0, sourceWidth, sourceHeight);
    const pixels = maskContext.getImageData(
      0,
      0,
      sourceWidth,
      sourceHeight
    ).data;

    const spacing = Math.max(3, width / 96);
    const radiusX = Math.max(
      1,
      Math.round((spacing / width) * sourceWidth * 0.42)
    );
    const radiusY = Math.max(
      1,
      Math.round((spacing / height) * sourceHeight * 0.42)
    );
    fontSize = Math.max(2.6, spacing * 0.76);
    particles = [];
    particleGroups = [[], [], []];

    for (let y = spacing / 2; y < height; y += spacing) {
      for (let x = spacing / 2; x < width; x += spacing) {
        let peak = 0;
        const centerX = Math.round((x / width) * (sourceWidth - 1));
        const centerY = Math.round((y / height) * (sourceHeight - 1));
        for (let oy = -radiusY; oy <= radiusY; oy += 2) {
          const sy = Math.max(0, Math.min(sourceHeight - 1, centerY + oy));
          for (let ox = -radiusX; ox <= radiusX; ox += 2) {
            const sx = Math.max(0, Math.min(sourceWidth - 1, centerX + ox));
            const i = (sy * sourceWidth + sx) * 4;
            peak = Math.max(peak, pixels[i], pixels[i + 1], pixels[i + 2]);
          }
        }

        if (peak <= 9) continue;
        const brightness = Math.min(1, Math.pow((peak - 7) / 248, 0.72));
        const faceDistance = Math.hypot(
          (x / width - 0.5) / 0.23,
          (y / height - 0.29) / 0.3
        );
        const faceFocus = Math.max(0, 1 - faceDistance);
        let bucket = brightness > 0.52 ? 2 : brightness > 0.17 ? 1 : 0;
        if (faceFocus > 0.35 && brightness > 0.12) {
          bucket = 2;
        } else if (faceFocus > 0.2 && brightness > 0.1) {
          bucket = Math.max(bucket, 1);
        }

        const particle = {
          x,
          y,
          renderX: x,
          renderY: y,
          seed: hash(x, y, 1),
          faceFocus,
          speed: 0.65 + hash(x, y, 2) * 1.25,
          orbit: 0.45 + hash(x, y, 3) * 1.4,
          glyph: GLYPHS[Math.floor(hash(x, y, 4) * GLYPHS.length)],
        };
        particles.push(particle);
        particleGroups[bucket].push(particle);
      }
    }
  };

  const positionParticles = (motion) => {
    const spread = 1.4 + motion * 3.2;
    const pointerX = (pointer.x + 0.5) * width;
    const pointerY = (pointer.y + 0.5) * height;
    const influenceRadius = width * 0.19;

    particles.forEach((particle) => {
      const stability = 1 - particle.faceFocus * 0.75;
      const waveX = Math.sin(
        phase * particle.speed + particle.seed * 13 + particle.y * 0.012
      );
      const waveY = Math.cos(
        phase * particle.speed * 0.79 + particle.seed * 19 + particle.x * 0.01
      );
      const pulse =
        Math.sin(phase * 0.71 + particle.seed * 7) * motion * stability;
      let x =
        particle.x +
        waveX * spread * particle.orbit * stability +
        ((particle.x - width * 0.5) / width) * pulse * 7;
      let y =
        particle.y +
        waveY * spread * (0.55 + particle.orbit * 0.45) * stability +
        ((particle.y - height * 0.45) / height) * pulse * 5;

      if (pointer.active) {
        const dx = particle.x - pointerX;
        const dy = particle.y - pointerY;
        const distance = Math.hypot(dx, dy) || 1;
        const influence = Math.max(0, 1 - distance / influenceRadius);
        x += (dx / distance) * influence * motion * 18;
        y += (dy / distance) * influence * motion * 18;
      }

      particle.renderX = x;
      particle.renderY = y;
    });
  };

  const paint = () => {
    context.clearRect(0, 0, width, height);
    context.font = `${fontSize}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = inkGlyph;
    particleGroups.forEach((group, bucket) => {
      context.globalAlpha = BUCKET_OPACITY[bucket];
      group.forEach((particle) => {
        context.fillText(particle.glyph, particle.renderX, particle.renderY);
      });
    });
    context.globalAlpha = 1;
  };

  const drawFrame = (time) => {
    frame = window.requestAnimationFrame(drawFrame);
    if (time - lastRenderedTime < 40) return;

    const elapsed = Math.max(0, time - previousTime);
    const delta = Math.min(50, elapsed);
    previousTime = time;
    lastRenderedTime = time;

    motionElapsed += elapsed * 0.001;
    // Five seconds up to 2x, five seconds back down, then repeat.
    const cyclePosition = (motionElapsed % 10) / 5;
    const cycleAmount = cyclePosition <= 1 ? cyclePosition : 2 - cyclePosition;
    const motion = 0.1 + cycleAmount * 1.9;

    phase += delta * 0.001 * motion;
    pointer.x += (pointer.targetX - pointer.x) * 0.06;
    pointer.y += (pointer.targetY - pointer.y) * 0.06;

    if (imageReady && particles.length) {
      positionParticles(motion);
      paint();
    }
  };

  // One composed frame at rest — for reduced-motion/data and the off-screen case.
  const renderStill = () => {
    if (!imageReady || !particles.length) return;
    phase = 0;
    positionParticles(0.12);
    paint();
  };

  const stop = () => {
    if (!frame) return;
    window.cancelAnimationFrame(frame);
    frame = 0;
  };

  const start = () => {
    if (wantsStill()) {
      stop();
      renderStill();
      return;
    }
    if (frame || !visible) return;
    previousTime = performance.now();
    frame = window.requestAnimationFrame(drawFrame);
  };

  const resize = () => {
    const bounds = host.getBoundingClientRect();
    if (bounds.width < 2 || bounds.height < 2) return;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = Math.round(bounds.width);
    height = Math.round(bounds.height);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    readColors();
    buildParticles();
  };

  host.addEventListener(
    "pointermove",
    (event) => {
      const bounds = host.getBoundingClientRect();
      pointer.active = true;
      pointer.targetX = (event.clientX - bounds.left) / bounds.width - 0.5;
      pointer.targetY = (event.clientY - bounds.top) / bounds.height - 0.5;
    },
    { passive: true }
  );
  host.addEventListener(
    "pointerleave",
    () => {
      pointer.active = false;
      pointer.targetX = 0;
      pointer.targetY = 0;
    },
    { passive: true }
  );

  const onImageReady = () => {
    imageReady = true;
    resize();
    start();
  };

  if (image.complete && image.naturalWidth > 0) {
    onImageReady();
  } else {
    image.addEventListener("load", onImageReady, { once: true });
    image.addEventListener(
      "error",
      () => console.warn("[cipher-portrait] portrait image failed to load"),
      { once: true }
    );
  }

  if ("ResizeObserver" in window) {
    new ResizeObserver(() => {
      resize();
      start();
    }).observe(host);
  } else {
    window.addEventListener("resize", resize, { passive: true });
  }

  // Don't spend a single frame animating a portrait no one is looking at.
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { threshold: 0.02 }
    ).observe(host);
  }

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });

  const onPreferenceChange = () => {
    stop();
    start();
  };
  reducedMotion.addEventListener?.("change", onPreferenceChange);
  reducedData.addEventListener?.("change", onPreferenceChange);
}
